import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

const startGoogleAuth = () => {
  const redirect = `${window.location.origin}/auth/callback`;
  window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
};

export default function AuthPage({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name);
        toast.success("Account created");
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(typeof msg === "string" ? msg : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="auth-page">
      {/* Left: Wall Street image */}
      <div
        className="hidden lg:block relative bg-stone_brand-dark"
        style={{
          backgroundImage:
            "linear-gradient(rgba(26, 36, 33, 0.6), rgba(26, 36, 33, 0.8)), url('https://images.pexels.com/photos/34032677/pexels-photo-34032677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="text-bone-50 font-serif-display text-2xl tracking-tight">
            Fairvest
          </Link>
          <div className="text-bone-50 max-w-md">
            <div className="overline text-bone-50/70 mb-4">— Equity Research</div>
            <p className="font-serif-display text-4xl leading-tight italic">
              "Price is what you pay. Value is what you get."
            </p>
            <p className="text-bone-50/70 mt-4 text-sm">— Warren Buffett</p>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-8 bg-bone-50">
        <div className="w-full max-w-md">
          <div className="overline mb-3">— {mode === "login" ? "Welcome Back" : "Get Started"}</div>
          <h1 className="font-serif-display text-4xl text-stone_brand-dark mb-2 font-semibold tracking-tight">
            {mode === "login" ? "Sign in." : "Create account."}
          </h1>
          <p className="text-sm text-stone_brand mb-10">
            {mode === "login"
              ? "Access your watchlist and saved analyses."
              : "Start comparing stocks on fair value in under a minute."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <Label className="overline mb-2 block">Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="rounded-sm border-line bg-white focus-visible:ring-moss"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <Label className="overline mb-2 block">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-sm border-line bg-white focus-visible:ring-moss"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <Label className="overline mb-2 block">Password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm border-line bg-white focus-visible:ring-moss"
                data-testid="auth-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-11"
              data-testid="auth-submit-btn"
            >
              {loading ? "Working…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-line" />
            <span className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">or</span>
            <div className="flex-1 h-px bg-line" />
          </div>

          <button
            type="button"
            onClick={startGoogleAuth}
            className="w-full h-11 rounded-sm border border-line bg-white hover:bg-bone hover:border-stone_brand text-stone_brand-dark text-sm font-medium inline-flex items-center justify-center gap-2 transition"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8L6.2 32.9C9.6 39.4 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.6l6.2 5.2C41.6 34.7 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="mt-8 text-sm text-stone_brand text-center">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button
                  onClick={() => setMode("signup")}
                  className="text-moss hover:text-moss-hover font-medium underline underline-offset-4"
                  data-testid="switch-to-signup"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already a member?{" "}
                <button
                  onClick={() => setMode("login")}
                  className="text-moss hover:text-moss-hover font-medium underline underline-offset-4"
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
